import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "super_admin" | "admin" | "partner" | "customer";

export interface NotificationPrefs {
  email_enquiries: boolean;
  email_listing_updates: boolean;
  email_contact_requests: boolean;
  email_marketing: boolean;
  sms_enquiries: boolean;
  sms_listing_updates: boolean;
  sms_contact_requests: boolean;
}

export interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  phone: string | null;
  city: string | null;
  avatar_url: string | null;
  partner_type: string | null;
  verification_status: string;
  notification_prefs?: NotificationPrefs | null;
}

interface AuthCtx {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  loading: boolean;
  hasRole: (r: AppRole) => boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthCtx | null>(null);

// Guards against duplicate login entries when SIGNED_IN re-fires for the same user.
let loggedInFor: string | null = null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  const loadProfileAndRoles = async (uid: string) => {
    const [{ data: p }, { data: r }] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", uid),
    ]);
    setProfile((p as Profile) ?? null);
    setRoles(((r as { role: AppRole }[]) ?? []).map((x) => x.role));
  };

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        if (event === "SIGNED_IN" && loggedInFor !== s.user.id) {
          loggedInFor = s.user.id;
          setTimeout(() => {
            void import("@/lib/backend").then((m) =>
              m.recordAudit({ action: "login", entityType: "session", entityId: s.user.id }),
            );
          }, 0);
        }
        setTimeout(() => loadProfileAndRoles(s.user.id), 0);
      } else {
        setProfile(null);
        setRoles([]);
      }
    });

    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      if (data.session?.user) await loadProfileAndRoles(data.session.user.id);
      setLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const value: AuthCtx = {
    user,
    session,
    profile,
    roles,
    loading,
    hasRole: (r) => roles.includes(r),
    signOut: async () => {
      // Log before the session goes away — audit inserts need auth.uid().
      const { recordAudit } = await import("@/lib/backend");
      await recordAudit({ action: "logout", entityType: "session", entityId: user?.id ?? null });
      loggedInFor = null;
      try {
        await supabase.auth.signOut();
      } catch {
        // Network/4xx on sign-out must never bubble — the local session is
        // cleared either way and the caller navigates to /login.
      }
    },
    refreshProfile: async () => {
      if (user) await loadProfileAndRoles(user.id);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export const ROLE_LABELS: Record<AppRole, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  partner: "Partner",
  customer: "Customer",
};

export const ROLE_HOME: Record<AppRole, string> = {
  super_admin: "/super-admin/dashboard",
  admin: "/admin/dashboard",
  partner: "/partner/dashboard",
  customer: "/customer/dashboard",
};
